import { Button } from '@calendly/ui/components/button'
import { BodyText, LabelText } from '@calendly/ui/components/typography'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useGetUserQuery, useUpdateUserActivationDataMutation } from 'common'

import {
  clearBrandRefreshActivationData,
  patchBrandRefreshActivationData,
} from './brandRefreshActivationData'
import {
  BRAND_REFRESH_DEBUG_UPDATED_EVENT,
  clearBrandRefreshDebugOverrides,
  isBrandRefreshDebugActive,
  notifyBrandRefreshDebugUpdated,
  readBrandRefreshDebugOverrides,
  setBrandRefreshDebugCountdownDays,
  setBrandRefreshDebugStartedDaysAgo,
} from './brandRefreshBetaDebug'
import { getCountdownFromStartedAt } from './brandRefreshCountdown'
import { useBrandRefreshActivationData } from './useBrandRefreshActivationData'
import {
  debugPanelActionsCss,
  debugPanelCountdownOptionsCss,
  debugPanelStatusCss,
} from '../debug_tools/debugPanelStyles'
import {
  clearProductRefreshPreviewFromStorage,
  useProductRefreshPreviewContext,
} from '../product_refresh/productRefreshPreviewContext'

const BRAND_REFRESH_DEBUG_COUNTDOWN_PRESETS = [1, 3, 7] as const

/**
 * Beta banner countdown debug section (panel UI only).
 * `window.brandRefreshBetaDebug` is installed by {@link BrandRefreshBetaDebugConsoleShim}.
 * Title is supplied by the debug registry registration.
 */
export const BrandRefreshBannerDebugSection = () => {
  const [debugRevision, setDebugRevision] = useState(0)
  const { blob } = useBrandRefreshActivationData({ skip: !isBrandRefreshDebugActive() })
  const { setOverride: setProductRefreshPreviewOverride } = useProductRefreshPreviewContext()
  const { refetch: refetchUser } = useGetUserQuery(undefined)
  const [updateActivationData] = useUpdateUserActivationDataMutation()

  const serverStartedAt = blob.banner_started_at
  const debug = readBrandRefreshDebugOverrides(debugRevision)
  const effectiveStartedAt = debug.startedAt ?? serverStartedAt ?? null

  useEffect(() => {
    const refreshDebug = () => setDebugRevision((value) => value + 1)

    window.addEventListener(BRAND_REFRESH_DEBUG_UPDATED_EVENT, refreshDebug)
    return () => {
      window.removeEventListener(BRAND_REFRESH_DEBUG_UPDATED_EVENT, refreshDebug)
    }
  }, [])

  const { daysRemaining, isExpired } = useMemo(
    () => getCountdownFromStartedAt(effectiveStartedAt, {
      countdownDays: debug.countdownDays,
      forceExpired: debug.forceExpired,
    }),
    [debug.countdownDays, debug.forceExpired, effectiveStartedAt],
  )

  const persistStartedDaysAgo = useCallback(async (daysAgo: number) => {
    const startedAt = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString()
    await updateActivationData((data) => patchBrandRefreshActivationData(
      data,
      { banner_started_at: startedAt },
    ))
    setBrandRefreshDebugStartedDaysAgo(daysAgo)
  }, [updateActivationData])

  const persistStartedNow = useCallback(async () => {
    const startedAt = new Date().toISOString()
    clearBrandRefreshDebugOverrides()
    await updateActivationData((data) => patchBrandRefreshActivationData(
      data,
      { banner_started_at: startedAt },
    ))
    notifyBrandRefreshDebugUpdated()
  }, [updateActivationData])

  const handleStartNow = useCallback(() => {
    persistStartedNow().catch(() => {})
  }, [persistStartedNow])

  const handleExpire = useCallback(() => {
    persistStartedDaysAgo(debug.countdownDays + 1).catch(() => {})
  }, [debug.countdownDays, persistStartedDaysAgo])

  const handleSetCountdownDays = useCallback((countdownDays: number) => {
    setBrandRefreshDebugCountdownDays(countdownDays)
  }, [])

  const clearActivationDataAndOverrides = useCallback(async () => {
    clearBrandRefreshDebugOverrides()
    clearProductRefreshPreviewFromStorage()
    setProductRefreshPreviewOverride(false)

    try {
      await updateActivationData(clearBrandRefreshActivationData).unwrap()
      notifyBrandRefreshDebugUpdated()
    } catch {
      await refetchUser()
    }
  }, [refetchUser, setProductRefreshPreviewOverride, updateActivationData])

  const handleClear = useCallback(() => {
    clearActivationDataAndOverrides().catch(() => {})
  }, [clearActivationDataAndOverrides])

  return (
    <>
      <div className={debugPanelStatusCss}>
        <BodyText size="x-small">
          {daysRemaining}
          d left /
          {' '}
          {debug.countdownDays}
          d timer
          {isExpired ? ' (expired)' : ''}
        </BodyText>
      </div>
      <LabelText size="small">Timer (days)</LabelText>
      <div className={debugPanelCountdownOptionsCss}>
        {BRAND_REFRESH_DEBUG_COUNTDOWN_PRESETS.map((countdownDays) => (
          <Button
            key={countdownDays}
            size="small"
            decoration={debug.countdownDays === countdownDays ? 'primary' : 'secondary-outline'}
            onClick={() => handleSetCountdownDays(countdownDays)}
            data-testid={`brand-refresh-debug-countdown-${countdownDays}d`}
          >
            {countdownDays}
            d
          </Button>
        ))}
      </div>
      <div className={debugPanelActionsCss}>
        <Button size="small" decoration="secondary-outline" onClick={handleStartNow}>
          Start now
        </Button>
        <Button
          size="small"
          decoration="secondary-outline"
          onClick={handleExpire}
        >
          Expire
        </Button>
        <Button
          size="small"
          decoration="secondary-outline"
          onClick={handleClear}
        >
          Clear
        </Button>
      </div>
    </>
  )
}
